import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Radio,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useOrgScopedQueryKey } from "../../query/scope";
import { useMyPermissions } from "../permissions/useMyPermissions";
import {
  deleteCoderSystem,
  setCoderSystem,
  HTTP_UNPROCESSABLE_ENTITY,
  HttpError,
  type CoderProbeStatusResponse,
  type CoderSystemConfigResponse,
} from "./api";
import {
  InstallHintCommands,
  OverridePathForm,
  ProviderRow,
  RecoveryFlow,
  type RecoveryOption,
} from "./CoderSystemParts";
import {
  CODER_SYSTEM_CHOICE_KEY,
  useCoderProviders,
  useCoderSystemChoice,
  useCoderSystemStatus,
} from "./useCoderSystem";

// Секция «Система-кодер» на `/settings/connections` (ADR-0074).
//
// Выбор кодера НЕ подсвечен по умолчанию: `coder_kind = null` до
// явного выбора пользователем, задачи не запускаются. `GET /status`
// сейчас всегда отдаёт `runner_available=false` — у backend нет канала
// к бинарям раннера; секция в этом случае не рисует бейджи и кнопку
// «Перепроверить», а показывает текст «наличие бинаря не проверено» и
// даёт сохранить путь без проверки.

interface SetCoderSystemVariables {
  readonly coder_kind: string;
  readonly override_path?: string | null;
}

export function CoderSystemSection(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Право — подсказка интерфейсу: неизвестный набор не блокирует
  // контролы, отказ сервера по-прежнему приходит как `saveError`.
  const readOnly = useMyPermissions().isDenied("provider.manage");

  const providers = useCoderProviders();
  const status = useCoderSystemStatus();
  const choice = useCoderSystemChoice();
  // Полный ключ кэша `choice` ТЕКУЩЕЙ организации — тем же способом
  // построения, что у `useCoderSystemChoice` (`src/query/scope.ts`).
  // Запись мутаций ниже целит в этот один ключ (`setQueryData`), а не
  // префиксный `setQueriesData`, который задел бы кэш любой организации.
  //
  // `useOrgScopedQueryKey` возвращает `null` без активной организации —
  // но `choice` сам org-scoped (гейт `choice.isPending` ниже), а кнопки,
  // вызывающие мутации, рисуются только после его успеха. К моменту,
  // когда `onSuccess` ниже реально вызовется, организация обязана быть
  // активна — иначе и сам `choice` не загрузился бы. Утверждение
  // (`!`), а не веточный `if`, чтобы не заводить непроверяемую ветку
  // «нет организации», которая здесь физически не достижима.
  const choiceQueryKey = useOrgScopedQueryKey(CODER_SYSTEM_CHOICE_KEY)!;

  // `null` — путь ещё не правили в этом рендере формы; тогда поле
  // показывает сохранённый `override_path`.
  const [pathDraft, setPathDraft] = useState<string | null>(null);
  const [pendingKind, setPendingKind] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<RecoveryOption>("hint");
  const [saveError, setSaveError] = useState<string | null>(null);

  const describeError = (err: unknown): string => {
    if (err instanceof HttpError && err.status === HTTP_UNPROCESSABLE_ENTITY) {
      return t("connections.coderSystem.saveRejected");
    }
    const message = err instanceof Error ? err.message : String(err);
    return t("connections.genericError", { message });
  };

  // Ключ кэша фиксируется в `onMutate` и приходит в `onSuccess` контекстом:
  // ответ пишется в кэш той организации, в которой запрос отправлен, даже
  // если организацию сменили, пока запрос был в полёте.
  const save = useMutation<
    CoderSystemConfigResponse,
    Error,
    SetCoderSystemVariables,
    readonly unknown[]
  >({
    mutationFn: (body) => setCoderSystem(body),
    onMutate: () => choiceQueryKey,
    onSuccess: (data, _variables, key) => {
      queryClient.setQueryData(key, data);
      setPathDraft(null);
      setSaveError(null);
    },
    onError: (err) => {
      setSaveError(describeError(err));
    },
  });

  const reset = useMutation<void, Error, void, readonly unknown[]>({
    mutationFn: () => deleteCoderSystem(),
    onMutate: () => choiceQueryKey,
    onSuccess: (_data, _variables, key) => {
      const cleared: CoderSystemConfigResponse = {
        coder_kind: null,
        override_path: null,
        is_explicit: false,
      };
      queryClient.setQueryData(key, cleared);
      setPathDraft(null);
      setSaveError(null);
    },
    onError: (err) => {
      setSaveError(describeError(err));
    },
  });

  if (providers.isPending || choice.isPending || status.isPending) {
    return <Loader />;
  }

  if (providers.isError || choice.isError) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("connections.coderSystem.title")}</Title>
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
      </Stack>
    );
  }

  const providerList = providers.data;
  // Ошибка `/status` равносильна «сведений о бинарях нет» — секция не
  // падает, просто не показывает probe-данные.
  const runnerAvailable = status.isSuccess && status.data.runner_available;
  const statusByKind = runnerAvailable
    ? new Map<string, CoderProbeStatusResponse>(
        status.data.coders.map((coder) => [coder.coder_kind, coder]),
      )
    : new Map<string, CoderProbeStatusResponse>();

  const selectedKind = choice.data.coder_kind;
  const selectedProvider = providerList.find(
    (p) => p.coder_kind === selectedKind,
  );
  const unavailableKind =
    selectedKind !== null && selectedProvider === undefined;

  const handleSelect = (kind: string): void => {
    setSaveError(null);
    if (choice.data.override_path !== null) {
      setPendingKind(kind);
      return;
    }
    save.mutate({ coder_kind: kind });
  };

  const handleCancelChangeConfirm = (): void => {
    setPendingKind(null);
  };

  const handleConfirmChange = (kind: string): void => {
    save.mutate({ coder_kind: kind });
    setPendingKind(null);
  };

  const pathValue = pathDraft ?? choice.data.override_path ?? "";

  const handleSavePath = (kind: string): void => {
    setSaveError(null);
    save.mutate({ coder_kind: kind, override_path: pathValue });
  };

  const handleRescan = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ["connections", "coder-system", "status"],
    });
  };

  return (
    <Stack gap="md">
      <Title order={2}>{t("connections.coderSystem.title")}</Title>

      {!runnerAvailable ? (
        <Alert
          color="gray"
          title={t("connections.coderSystem.unverifiedHeading")}
        >
          {t("connections.coderSystem.unverifiedText")}
        </Alert>
      ) : null}

      {selectedKind === null ? (
        <Text>{t("connections.coderSystem.notSelected")}</Text>
      ) : null}

      {unavailableKind ? (
        <Alert color="red">
          {t("connections.coderSystem.unavailableChoice", {
            kind: selectedKind,
          })}
        </Alert>
      ) : null}

      {readOnly ? (
        <Text size="sm" c="dimmed">
          {t("connections.coderSystem.readOnly")}
        </Text>
      ) : null}

      {providerList.length === 0 ? (
        <Text>{t("connections.coderSystem.empty")}</Text>
      ) : (
        <Radio.Group
          value={selectedProvider === undefined ? null : selectedKind}
          onChange={handleSelect}
          disabled={readOnly}
          label={t("connections.coderSystem.chooseLabel")}
        >
          <Stack gap="sm" mt="xs">
            {providerList.map((provider) => (
              <ProviderRow
                key={provider.coder_kind}
                provider={provider}
                probe={statusByKind.get(provider.coder_kind)}
                showStatus={runnerAvailable}
              />
            ))}
          </Stack>
        </Radio.Group>
      )}

      {runnerAvailable ? (
        <Group>
          <Button
            variant="default"
            onClick={handleRescan}
            loading={status.isFetching}
          >
            {t("connections.coderSystem.rescanButton")}
          </Button>
        </Group>
      ) : null}

      {saveError !== null ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {saveError}
        </Alert>
      ) : null}

      {selectedProvider !== undefined && !runnerAvailable ? (
        <Stack gap="md">
          <InstallHintCommands installHint={selectedProvider.install_hint} />
          <OverridePathForm
            value={pathValue}
            onChange={setPathDraft}
            onSave={() => {
              handleSavePath(selectedProvider.coder_kind);
            }}
            pending={save.isPending}
            disabled={readOnly}
          />
        </Stack>
      ) : null}

      {selectedProvider !== undefined &&
      runnerAvailable &&
      statusByKind.get(selectedProvider.coder_kind)?.status === "not_found" ? (
        <RecoveryFlow
          installHint={selectedProvider.install_hint}
          recovery={recovery}
          onRecoveryChange={setRecovery}
          pathValue={pathValue}
          onPathChange={setPathDraft}
          onSavePath={() => {
            handleSavePath(selectedProvider.coder_kind);
          }}
          savePending={save.isPending}
          disabled={readOnly}
        />
      ) : null}

      {selectedKind !== null ? (
        <Group>
          <Button
            variant="default"
            color="red"
            onClick={() => {
              reset.mutate();
            }}
            loading={reset.isPending}
            disabled={readOnly}
          >
            {t("connections.coderSystem.resetButton")}
          </Button>
        </Group>
      ) : null}

      <Modal
        opened={pendingKind !== null}
        onClose={handleCancelChangeConfirm}
        title={t("connections.coderSystem.changeConfirmTitle")}
        centered
      >
        {pendingKind !== null ? (
          <Stack gap="md">
            <Text>{t("connections.coderSystem.changeConfirmText")}</Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={handleCancelChangeConfirm}>
                {t("connections.modalCancel")}
              </Button>
              <Button
                onClick={() => {
                  handleConfirmChange(pendingKind);
                }}
              >
                {t("connections.modalConfirm")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}
